import { User as ConfigCatUser } from '@configcat/sdk';
import { useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';

import { getConfigCatClient } from './client';
import { FlagName, FlagValue, flags } from './flags';
import { useTransitiveMemberOf } from './graph/useTransitiveMemberOf';
import { testUserGroupEmails } from './testUsers/groupEmails';

const isUserAuthorized = (email?: string): email is NonNullable<typeof email> => Boolean(email);

const useGroupEmails = (): { data?: string[]; isLoading: boolean } => {
	const { data: session } = useSession();
	const res = useTransitiveMemberOf();
	if (res.isLoading) return { isLoading: true };
	if (session?.isTestUser) {
		return { data: testUserGroupEmails, isLoading: false };
	}
	return {
		data: (res.data || []).map((g) => g.mail).filter((v): v is NonNullable<typeof v> => Boolean(v)),
		isLoading: false,
	};
};

export const useFeatureFlag = <T extends FlagName>(flagName: T): [FlagValue<T>, boolean] => {
	const { data: session, status } = useSession();
	const email = session?.user?.email?.toLowerCase();
	const defaultValue = flags[flagName].defaultValue as FlagValue<T>;
	const [flag, setFlag] = useState<FlagValue<T>>(defaultValue);
	const [loading, setLoading] = useState(true);
	const { data: groupEmails, isLoading: isGroupEmailsLoading } = useGroupEmails();
	const groupEmailsStr = groupEmails?.join(';');

	useEffect(() => {
		if (isGroupEmailsLoading) return;

		const client = getConfigCatClient();
		const fetchFlag = async () => {
			if (isUserAuthorized(email)) {
				const user: ConfigCatUser = {
					identifier: email,
					email,
					// - named groupAddresses to be consistent with MKT ConfigCat
					// - add '-' b/c configcat warns for empty value
					custom: { groupAddresses: groupEmailsStr || '-' },
				};
				const flagValue = await client.getValueAsync(flagName, defaultValue, user);
				setFlag(flagValue as FlagValue<T>);
				setLoading(false);
			} else {
				setFlag(defaultValue);
				// don't set loading=false instantly here to avoid blinking
				setLoading(status === 'loading');
			}
		};

		fetchFlag();
		client.on('configChanged', fetchFlag);

		return () => {
			client.off('configChanged', fetchFlag);
		};
	}, [flagName, defaultValue, email, groupEmailsStr, status, isGroupEmailsLoading]);

	return [flag, loading];
};
